<template>
  <UiModal
    ref="modalRef"
    v-model="isOpen"
    :title="modalTitle"
    size="xlarge"
    :show-footer="true"
    :show-close-button="true"
    :before-close="handleBeforeClose"
    class="step07-eval-target-config-modal"
    @update:modelValue="v => !v && emit('close')"
  >
    <template #title>
      <span class="etcm__modal-title">{{ modalTitle }}</span>
    </template>
    <!--
      레거시 JSP: 단일 편집(#evalTargetConfig, loadEvalTargetConfig)과 일괄(#evalTargetConfigAll, loadEvalTargetConfigAll)은
      마크업이 다름(단일은 매니저·동료·후배 등 추가). 본 모달은 일괄 패널과 동일 UI·saveEvalTargetAllForAdmin 이며,
      단일 행일 때 evalTargetId + evalTargetList 1건으로 대상만 갱신한다.
      진행현황(hreStatusList.jsp) 일괄 조회: hreTargetMng/evalTargetConfigForAdmin 에 findYear·evalCycleCd·evalTargetId="" 만 전달(evalGrpId 없음).
      저장 시에는 evalTargetList 각 행의 evalGrpId·managerId로 평가군이 섞여 있어도 처리(hreStatus saveEvalTargetConfAll).
    -->
    <div v-if="loading" class="etcm__loading">{{ st('m_a985a9f1cc') }}</div>
    <div v-else-if="errorMessage" class="etcm__error">{{ errorMessage }}</div>
    <div v-else class="etcm__body">
      <!-- 단일 평가대상자: 부서장 -->
      <section v-if="isSingleTarget" class="etcm__card">
        <header class="etcm__card-header">
          <h4 class="etcm__card-title">{{ st('m_1339480292') }}</h4>
        </header>
        <div class="etcm__card-body">
          <!-- <div class="etcm__target-summary">
            <span class="etcm__target-summary-label">{{ t('hrEvaluation.conf.targets.evalTargetsTitle') }}</span>
            <strong class="etcm__target-summary-name">{{ singleTargetName || '-' }}</strong>
          </div> -->
          <div class="etcm__evaluator-row">
            <span class="etcm__evaluator-label">{{ st('m_1339480292') }}</span>
            <div class="etcm__user-field">
              <div class="etcm__user-display" :class="{ 'etcm__user-display--empty': !managerLocal.userNm }">
                {{ managerLocal.userNm || st('targets.unassigned') }}
              </div>
              <UiButton variant="secondary-line" size="small" icon-only @click="openUserPicker('manager')">
                <i class="icon icon-md icon-search" aria-hidden="true" />
              </UiButton>
              <UiButton v-if="managerLocal.userId" variant="secondary-line" size="small" icon-only @click="clearEvalUser('manager')">×</UiButton>
            </div>
            <UiButton :variant="applyButtonVariant('manager')" size="small" :disabled="isEvalUserApplyDisabled('manager')" @click="onApplyEvalUser('manager')">{{ t('hrEvaluation.conf.basis.common.action.apply') }}</UiButton>
          </div>
        </div>
      </section>

      <!-- 평가자 카드 -->
      <section v-if="hasEvaluatorSection" class="etcm__card">
        <header class="etcm__card-header">
          <h4 class="etcm__card-title">{{ t('hrEvaluation.conf.targets.evaluatorsTitle') }}</h4>
        </header>
        <div class="etcm__card-body">
          <div v-for="(row, rIdx) in evaluatorRows" :key="row.role" class="etcm__evaluator-row" :class="{ 'etcm__evaluator-row--multi': row.multiple }">
            <span class="etcm__evaluator-badge">{{ rIdx + 1 }}</span>
            <span class="etcm__evaluator-label">{{ row.label.replace(st('misc.superiorEvalSuffix'), '') }}</span>
            <div class="etcm__user-field">
              <template v-if="row.multiple">
                <UiMultiSelect
                  :model-value="getMultiEvalUserIds(row.role)"
                  :options="multiEvalUserSelectOptions"
                  :placeholder="t('hrEvaluation.conf.targets.searchSelectPh')"
                  searchable
                  size="small"
                  class="etcm__multi-eval-select"
                  @update:model-value="ids => onMultiEvalUserIdsChange(row.role, ids)"
                />
                <UiButton variant="secondary-line" size="small" icon-only @click="openUserPicker(row.role)">
                  <i class="icon icon-md icon-search" aria-hidden="true" />
                </UiButton>
              </template>
              <template v-else>
                <div class="etcm__user-display" :class="{ 'etcm__user-display--empty': !row.userNm }">
                  {{ row.userNm || st('targets.unassigned') }}
                </div>
                <UiButton variant="secondary-line" size="small" icon-only @click="openUserPicker(row.role)">
                  <i class="icon icon-md icon-search" aria-hidden="true" />
                </UiButton>
                <UiButton v-if="row.userId" variant="secondary-line" size="small" icon-only @click="clearEvalUser(row.role)">×</UiButton>
              </template>
            </div>
            <UiButton :variant="applyButtonVariant(row.role)" size="small" :disabled="isEvalUserApplyDisabled(row.role)" @click="onApplyEvalUser(row.role)">{{ t('hrEvaluation.conf.basis.common.action.apply') }}</UiButton>
          </div>
        </div>
      </section>

      <!-- 성과평가 카드 -->
      <section v-if="showPerformSection" class="etcm__card" :class="{ 'etcm__card--inactive': !performYnSwitch }">
        <header class="etcm__card-header">
          <h4 class="etcm__card-title">{{ st('m_3adc3e3404') }}</h4>
          <div class="etcm__card-actions">
            <UiSwitch v-model="performYnSwitch" :disabled="!canUsePerformSwitch" />
            <span class="etcm__status-indicator" :class="{ 'etcm__status-indicator--active': performYnSwitch }">
              {{ performYnSwitch ? st('misc.statusActive') : st('misc.statusInactive') }}
            </span>
            <UiButton :variant="applyButtonVariant('perform')" size="small" :disabled="!canSave || !canUsePerformSwitch" @click="onApplyPerform">{{ t('hrEvaluation.conf.basis.common.action.apply') }}</UiButton>
          </div>
        </header>
        <div v-if="performYnSwitch || !hasPerformEvalType" class="etcm__card-body">
          <div v-if="!hasPerformEvalType" class="etcm__guide-message">{{ st('m_a1bb2fd8e3') }}</div>
          <div v-if="showResultRateRow" class="etcm__rate-row">
            <span class="etcm__ratio-kind">{{ t('hrEvaluation.conf.targets.result') }}</span>
            <UiInput
              v-model="performRateLocal.resultRate"
              type="text"
              maxlength="5"
              class="etcm__rate-input"
              @input="e => (performRateLocal.resultRate = inputIntegerOnly(e))"
            />
            <span class="etcm__pct">%</span>
            <UiSelect
              v-if="isResultMdlStmt"
              v-model="evalConfigLocal.resultPerfStmtId"
              :options="performStmtOptions"
              :placeholder="t('hrEvaluation.conf.targets.selectPlease')"
              size="small"
              style="flex: 1"
            />
          </div>
          <div v-if="showProcessRateRow" class="etcm__rate-row">
            <span class="etcm__ratio-kind">{{ t('hrEvaluation.conf.targets.process') }}</span>
            <UiInput
              v-model="performRateLocal.processRate"
              type="text"
              maxlength="5"
              class="etcm__rate-input"
              @input="e => (performRateLocal.processRate = inputIntegerOnly(e))"
            />
            <span class="etcm__pct">%</span>
            <UiSelect
              v-if="isProcessMdlStmt"
              v-model="evalConfigLocal.processPerfStmtId"
              :options="performStmtOptions"
              :placeholder="t('hrEvaluation.conf.targets.selectPlease')"
              size="small"
              style="flex: 1"
            />
          </div>
          <div v-if="showPerformSumIndicator" class="etcm__sum-indicator" :class="{ 'etcm__sum-indicator--ok': isPerformSumValid, 'etcm__sum-indicator--warn': !isPerformSumValid }">
            <Icon :name="isPerformSumValid ? 'mdi:check-circle' : 'mdi:alert-circle-outline'" size="16" />
            {{ st('misc.sumPctLabel', { pct: performSumPct }) }} {{ isPerformSumValid ? '' : st('misc.sumPctMust100') }}
          </div>
        </div>
      </section>

      <!-- 역량평가 카드 -->
      <section v-if="showCapaSection" class="etcm__card" :class="{ 'etcm__card--inactive': !capaYnSwitch }">
        <header class="etcm__card-header">
          <h4 class="etcm__card-title">{{ st('m_7f69bc4f72') }}</h4>
          <div class="etcm__card-actions">
            <UiSwitch v-model="capaYnSwitch" :disabled="!canUseCapaSwitch" />
            <span class="etcm__status-indicator" :class="{ 'etcm__status-indicator--active': capaYnSwitch }">
              {{ capaYnSwitch ? st('misc.statusActive') : st('misc.statusInactive') }}
            </span>
            <UiButton :variant="applyButtonVariant('capa')" size="small" :disabled="!canSave || !canUseCapaSwitch" @click="onApplyCapa">{{ t('hrEvaluation.conf.basis.common.action.apply') }}</UiButton>
          </div>
        </header>
        <div v-if="capaYnSwitch || !hasCapaGbnEditable" class="etcm__card-body">
          <div v-if="!hasCapaGbnEditable" class="etcm__guide-message">{{ st('m_e8789704b2') }}</div>
          <template v-else>
            <div v-for="(g, idx) in capaGbnEditable" :key="g.capaGbnId || idx" class="etcm__capa-accordion">
              <div class="etcm__capa-accordion-header">
                <button
                  type="button"
                  class="etcm__capa-accordion-toggle"
                  :aria-expanded="isCapaGroupExpanded(g.capaGbnId)"
                  @click="toggleCapaGroup(g.capaGbnId)"
                >
                  <Icon :name="isCapaGroupExpanded(g.capaGbnId) ? 'mdi:chevron-down' : 'mdi:chevron-right'" size="18" />
                </button>
                <span class="etcm__capa-gbn-name">{{ g.capaGbnNm || g.capaGbnId }}</span>
                <span class="etcm__capa-count">{{ capaSnapshotRowsForGbn(g).length }}개</span>
                <UiButton variant="secondary-line" size="small" icon-only :title="t('hrEvaluation.conf.targets.capaItemSelectTitle')" @click="openCapaItemPicker(g)">
                  <i class="icon icon-md icon-search" aria-hidden="true" />
                </UiButton>
                <div class="etcm__capa-rate">
                  <UiInput
                    v-model="g.capaGbnRate"
                    type="text"
                    :maxlength="CAPA_GBN_RATE_MAX_LENGTH"
                    class="etcm__rate-input"
                    @input="e => (g.capaGbnRate = inputCapaGbnRate(e))"
                  />
                  <span class="etcm__pct">%</span>
                </div>
              </div>
              <div v-if="isCapaGroupExpanded(g.capaGbnId) && capaSnapshotRowsForGbn(g).length" class="etcm__capa-chips">
                <span
                  v-for="(it, snapIdx) in capaSnapshotRowsForGbn(g)"
                  :key="capaSnapshotRowKey(it, snapIdx)"
                  class="etcm__capa-chip"
                >
                  {{ labelForCapaSnapshotItem(it) }}
                </span>
              </div>
            </div>
          </template>
        </div>
      </section>
    </div>

    <template #footerActions>
      <div class="etcm__footer-row">
        <UiCheckbox
          v-if="isSingleTarget && allEvalTargetRows.length > 1"
          v-model="applyAllTargets"
          :label="st('m_1d5b99ca31')"
          size="large"
        />
        <UiButton class="etcm__footer-close" variant="secondary" @click="requestClose">{{ st('m_218e2ae6c9') }}</UiButton>
      </div>
    </template>
  </UiModal>
  <!--
    UserSelectModal을 본 UiModal 내부(body)에 두면 바깥 모달 footer(닫기)가
    DOM 순서상 뒤에 있어 확인 버튼 클릭이 가려지거나 다른 레이어로 전달될 수 있음.
    형제로 분리해 스택킹/히트 테스트 충돌을 제거함.
  -->
  <UserSelectModal
    v-model="isUserSelectOpen"
    :mode="userPickMode"
    filter-type="all"
    :no-dim="true"
    :callback-component-name="userPickRole"
    :initial-selected-users="userPickInitialSelectedUsers"
    :find-year="String(props.findYear || '')"
    :eval-cycle-cd="String(props.evalCycleCd || '')"
    @confirm="onUserSelectConfirm"
  />
  <Step07CapaItemPickerModal
    v-model="isCapaItemPickerOpen"
    :find-year="String(props.findYear || '')"
    :eval-cycle-cd="String(props.evalCycleCd || '')"
    :eval-grp-id="String(props.evalGrpId || '')"
    :capa-gbn-row="capaItemPickerRow"
    :cmpt-grp-id="String(evalConfigLocal?.abilityCmptGrpId ?? evalConfigLocal?.cmptGrpId ?? '')"
    :preselected-items="capaItemPreselectedForPicker"
    :fallback-items="capaItemFallbackForPicker"
    @confirm="onCapaItemPickerConfirm"
  />
</template>

<script setup>
import { computed, ref, watch, unref, nextTick } from 'vue';
import UiModal from '~/components/common/UI/UiModal.vue';
import UiButton from '~/components/common/UI/UiButton.vue';
import UiInput from '~/components/common/UI/UiInput.vue';
import UiSelect from '~/components/common/UI/UiSelect.vue';
import UiMultiSelect from '~/components/common/UI/UiMultiSelect.vue';
import UserSelectModal from '~/components/user/UserSelectModal.vue';
import Step07CapaItemPickerModal from './Step07CapaItemPickerModal.vue';
import { useSettingsApi } from '~/composables/hr-evaluation/conf/settings/useSettingsApi';
import { useSettingsStore } from '~/composables/hr-evaluation/conf/settings/useSettingsStore';
import { getStep07PerformTypeFlags, getStep07RatioColumnFlags } from '~/composables/hr-evaluation/conf/settings/useStep07EvalRules';
import { useUserSelectorStore } from '~/composables/useUserSelectorStore.js';
import { showAlert, confirmAsync } from '~/composables/useConfirmDialog';
import { useCommon } from '~/composables/useCommon';
import { useHrEvalSettingsI18n } from '~/composables/hr-evaluation/conf/settings/useHrEvalSettingsI18n';
const { st, t } = useHrEvalSettingsI18n();

const { selectedUserObject: selectedUserObjectFromStore } = useUserSelectorStore();
const { inputIntegerOnly, inputUnsignedDecimal, getUnsignedDecimalMaxLength } = useCommon();

const CAPA_GBN_RATE_DECIMAL_LIMITS = { integerDigits: 3, fractionalDigits: 2 };
const CAPA_GBN_RATE_MAX_LENGTH = getUnsignedDecimalMaxLength(CAPA_GBN_RATE_DECIMAL_LIMITS);
const inputCapaGbnRate = e => inputUnsignedDecimal(e, CAPA_GBN_RATE_DECIMAL_LIMITS);
const { evalBaseData, evalData, evalTargetSelectionData, initializeEvalTargetSelectionData } = useSettingsStore();
const isLoading = useLoadingStore();

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  findYear: { type: String, default: '' },
  evalCycleCd: { type: String, default: '' },
  evalGrpId: { type: String, default: '' },
  /** Step07 평가대상 행 — 저장 API evalTargetList 구성용 (일괄: 전체, 단일 편집: 1건) */
  evalTargetRows: {
    type: Array,
    default: () => [],
  },
  /** 단일 모달에서 '모든 대상자에게 적용' 선택 시 저장 대상으로 사용할 전체 행 */
  allEvalTargetRows: {
    type: Array,
    default: () => [],
  },
  /** 단일 행 편집 시 evalTargetConfigForAdmin 조회용 (비어 있으면 일괄) */
  evalTargetId: { type: String, default: '' },
  /** 모달 제목 보조 — 단일 대상자명 */
  evalTargetNm: { type: String, default: '' },
  /**
   * 진행현황 등 설정 페이지 밖에서 열 때: useSettingsStore.evalBaseData 가 비어
   * 성과/역량 카드가 숨겨지는 것을 막기 위해 selectEvalBaseData 기준 시행 여부를 넘긴다.
   * 미전달(undefined)이면 기존처럼 evalBaseData만 사용.
   */
  performExecYn: { type: Boolean, default: undefined },
  abilityExecYn: { type: Boolean, default: undefined },
});

const emit = defineEmits(['update:modelValue', 'close', 'success']);

const { fetchEvalTargetConfigForAdmin, selectTargetStatusList, saveEvalTargetAllForAdmin } = useSettingsApi();

/** UiModal: 적용 저장 후 미저장 변경 감지용 텍스트 스냅샷 초기화 */
const modalRef = ref(null);

const isOpen = computed({
  get: () => props.modelValue,
  set: v => emit('update:modelValue', v),
});

/** 일괄: 일괄설정, 단일: 평가대상 설정 (+ 대상자명) */
const modalTitle = computed(() => {
  if (props.evalTargetId) {
    const nm = String(props.evalTargetNm || '').trim();
    return nm ? st('m_938229e595', { nm }) : st('m_d5accb8d41');
  }
  return st('m_a9ba39e6c4');
});

const loading = ref(false);
const errorMessage = ref('');
const applyAllTargets = ref(false);
const rowDirtyBaseline = ref({});

const evalConfigLocal = ref({});
const evalUsersLocal = ref({});
const managerLocal = ref({ userId: '', userNm: '' });
const multiEvalUsersLocal = ref({ mate: [], jnr: [] });
const evalTargetLocal = ref({ allPerformYn: 'Y', allCapaYn: 'Y' });
const performRateLocal = ref({ resultRate: '0', processRate: '0' });
const capaGbnEditable = ref([]);
const performEval = ref({});
const performTmpltList = ref([]);

const isUserSelectOpen = ref(false);
const userPickRole = ref('');
const multiEvalRoles = ['mate', 'jnr'];
const userPickMode = computed(() => (multiEvalRoles.includes(userPickRole.value) ? 'multi' : 'one'));
const userPickInitialSelectedUsers = computed(() => {
  if (!multiEvalRoles.includes(userPickRole.value)) return [];
  return multiEvalUsersLocal.value?.[userPickRole.value] || [];
});

const multiEvalUserSelectOptions = computed(() => {
  const list = evalTargetSelectionData.userList?.length ? evalTargetSelectionData.userList : evalTargetSelectionData.userDatas || [];
  const optionMap = new Map();
  list.forEach(item => {
    const value = String(item.userId ?? item.value ?? '').trim();
    if (!value) return;
    optionMap.set(value, {
      value,
      label: String(item.userNm ?? item.userDeptNm ?? item.label ?? value),
      department: item.userDeptNm ?? item.deptNm ?? '',
    });
  });
  multiEvalRoles.forEach(role => {
    (multiEvalUsersLocal.value[role] || []).forEach(user => {
      const value = String(user.userId ?? user.id ?? '').trim();
      if (!value || optionMap.has(value)) return;
      optionMap.set(value, {
        value,
        label: String(user.userNm ?? user.name ?? value),
        department: '',
      });
    });
  });
  return [...optionMap.values()];
});

const getMultiEvalUserIds = role =>
  (multiEvalUsersLocal.value?.[role] || []).map(u => String(u.userId ?? u.id ?? '')).filter(Boolean);

const onMultiEvalUserIdsChange = (role, ids) => {
  if (!multiEvalRoles.includes(role)) return;
  const idArr = Array.isArray(ids) ? ids.map(id => String(id)) : [];
  const optionMap = new Map(multiEvalUserSelectOptions.value.map(o => [String(o.value), o]));
  const prevMap = new Map((multiEvalUsersLocal.value[role] || []).map(u => [String(u.userId ?? u.id ?? ''), u]));
  const users = idArr.map(id => {
    const prev = prevMap.get(id);
    if (prev) return prev;
    const opt = optionMap.get(id);
    return { userId: id, userNm: opt?.label ?? id, name: opt?.label ?? id, label: opt?.label ?? id };
  });
  multiEvalUsersLocal.value = { ...multiEvalUsersLocal.value, [role]: uniqueUsers(users) };
};

const ynPerformOptions = [
  { value: 'Y', label: t('hrEvaluation.conf.targets.implementY') },
  { value: 'N', label: t('hrEvaluation.conf.targets.implementN') },
];

const ynCapaOptions = [
  { value: 'Y', label: t('hrEvaluation.conf.targets.implementY') },
  { value: 'N', label: t('hrEvaluation.conf.targets.implementN') },
];

const allEvalTargetRows = computed(() => (props.allEvalTargetRows || []).filter(row => row?.evalTargetId));
const effectiveEvalTargetRows = computed(() => {
  if (applyAllTargets.value && allEvalTargetRows.value.length) {
    return allEvalTargetRows.value;
  }
  return props.evalTargetRows || [];
});
const canSave = computed(() => effectiveEvalTargetRows.value.length > 0);
const isSingleTarget = computed(() => !!String(props.evalTargetId || '').trim());
const singleTargetName = computed(() => {
  const firstRow = (props.evalTargetRows || [])[0] || {};
  return String(props.evalTargetNm || firstRow.evalTargetNm || firstRow.evalTargetName || '').trim();
});
const isEvalUserApplyDisabled = role => {
  return !canSave.value || (applyAllTargets.value && ['manager'].includes(role));
};

const stableUsers = users =>
  (users || [])
    .map(user => ({
      userId: String(user.userId ?? ''),
      userNm: String(user.userNm ?? ''),
    }))
    .sort((a, b) => `${a.userId}:${a.userNm}`.localeCompare(`${b.userId}:${b.userNm}`));

const rowDirtyKeys = ['manager', 'fir', 'sec', 'thr', 'mate', 'jnr', 'perform', 'capa'];

const rowSnapshot = key => {
  const u = evalUsersLocal.value || {};
  if (key === 'manager') {
    return {
      userId: String(managerLocal.value.userId ?? ''),
      userNm: String(managerLocal.value.userNm ?? ''),
    };
  }
  if (key === 'fir') {
    return { userId: String(u.firEvalUserId ?? ''), userNm: String(u.firEvalUserNm ?? '') };
  }
  if (key === 'sec') {
    return { userId: String(u.secEvalUserId ?? ''), userNm: String(u.secEvalUserNm ?? '') };
  }
  if (key === 'thr') {
    return { userId: String(u.thrEvalUserId ?? ''), userNm: String(u.thrEvalUserNm ?? '') };
  }
  if (key === 'mate' || key === 'jnr') {
    return stableUsers(multiEvalUsersLocal.value?.[key] || []);
  }
  if (key === 'perform') {
    return {
      allPerformYn: String(evalTargetLocal.value.allPerformYn ?? ''),
      resultRate: String(performRateLocal.value.resultRate ?? ''),
      processRate: String(performRateLocal.value.processRate ?? ''),
      resultPerfStmtId: String((evalConfigLocal.value || {}).resultPerfStmtId ?? ''),
      processPerfStmtId: String((evalConfigLocal.value || {}).processPerfStmtId ?? ''),
    };
  }
  if (key === 'capa') {
    return {
      allCapaYn: String(evalTargetLocal.value.allCapaYn ?? ''),
      gbnList: (capaGbnEditable.value || [])
        .map(g => ({ capaGbnId: String(g.capaGbnId ?? ''), capaGbnRate: String(g.capaGbnRate ?? '') }))
        .sort((a, b) => a.capaGbnId.localeCompare(b.capaGbnId)),
      itemIds: effectiveCapaItemList.value
        .map(it => String(it.capaItemId ?? ''))
        .filter(Boolean)
        .sort(),
    };
  }
  return {};
};

const serializeRowSnapshot = key => JSON.stringify(rowSnapshot(key));

const resetRowDirty = key => {
  rowDirtyBaseline.value = {
    ...rowDirtyBaseline.value,
    [key]: serializeRowSnapshot(key),
  };
  nextTick(() => modalRef.value?.captureInitialTextValues?.());
};

const resetAllRowDirty = () => {
  const next = {};
  rowDirtyKeys.forEach(key => {
    next[key] = serializeRowSnapshot(key);
  });
  rowDirtyBaseline.value = next;
  nextTick(() => modalRef.value?.captureInitialTextValues?.());
};

/** 부분 적용 후 reload 시, 아직 적용하지 않은 dirty 행의 로컬 값을 보존하기 위한 스냅샷 */
const cloneDirtyLocalState = key => {
  if (key === 'manager') {
    return {
      userId: String(managerLocal.value.userId ?? ''),
      userNm: String(managerLocal.value.userNm ?? ''),
    };
  }
  if (key === 'fir' || key === 'sec' || key === 'thr') {
    const u = evalUsersLocal.value || {};
    return {
      userId: String(u[`${key}EvalUserId`] ?? ''),
      userNm: String(u[`${key}EvalUserNm`] ?? ''),
    };
  }
  if (key === 'mate' || key === 'jnr') {
    return (multiEvalUsersLocal.value?.[key] || []).map(user => ({
      userId: String(user.userId ?? ''),
      userNm: String(user.userNm ?? ''),
    }));
  }
  if (key === 'perform') {
    return {
      allPerformYn: String(evalTargetLocal.value.allPerformYn ?? ''),
      resultRate: String(performRateLocal.value.resultRate ?? ''),
      processRate: String(performRateLocal.value.processRate ?? ''),
      resultPerfStmtId: String((evalConfigLocal.value || {}).resultPerfStmtId ?? ''),
      processPerfStmtId: String((evalConfigLocal.value || {}).processPerfStmtId ?? ''),
    };
  }
  if (key === 'capa') {
    return {
      allCapaYn: String(evalTargetLocal.value.allCapaYn ?? ''),
      gbnList: (capaGbnEditable.value || []).map(g => ({ ...g })),
      items: (capaItemListSnapshot.value || []).map(it => ({ ...it })),
    };
  }
  return null;
};

const applyDirtyLocalState = (key, state) => {
  if (!state) return;
  if (key === 'manager') {
    managerLocal.value = { userId: state.userId || '', userNm: state.userNm || '' };
    return;
  }
  if (key === 'fir' || key === 'sec' || key === 'thr') {
    evalUsersLocal.value = {
      ...evalUsersLocal.value,
      [`${key}EvalUserId`]: state.userId || '',
      [`${key}EvalUserNm`]: state.userNm || '',
    };
    return;
  }
  if (key === 'mate' || key === 'jnr') {
    multiEvalUsersLocal.value = {
      ...multiEvalUsersLocal.value,
      [key]: Array.isArray(state) ? state.map(user => ({ ...user })) : [],
    };
    return;
  }
  if (key === 'perform') {
    evalTargetLocal.value = { ...evalTargetLocal.value, allPerformYn: state.allPerformYn };
    performRateLocal.value = {
      ...performRateLocal.value,
      resultRate: state.resultRate,
      processRate: state.processRate,
    };
    evalConfigLocal.value = {
      ...(evalConfigLocal.value || {}),
      resultPerfStmtId: state.resultPerfStmtId,
      processPerfStmtId: state.processPerfStmtId,
    };
    return;
  }
  if (key === 'capa') {
    evalTargetLocal.value = { ...evalTargetLocal.value, allCapaYn: state.allCapaYn };
    if (Array.isArray(state.gbnList) && state.gbnList.length) {
      const rateMap = Object.fromEntries(state.gbnList.map(g => [String(g.capaGbnId ?? ''), g]));
      capaGbnEditable.value = (capaGbnEditable.value || []).map(g => {
        const prev = rateMap[String(g.capaGbnId ?? '')];
        return prev ? { ...g, ...prev } : g;
      });
    }
    if (Array.isArray(state.items)) {
      capaItemListSnapshot.value = state.items.map(it => ({ ...it }));
    }
  }
};

/** 방금 적용한 행을 제외한 dirty 행 보존 */
const capturePreservedDirtyRows = exceptKey => {
  const preserved = {};
  rowDirtyKeys.forEach(key => {
    if (key === exceptKey) return;
    if (!isRowDirty(key)) return;
    preserved[key] = {
      baseline: rowDirtyBaseline.value?.[key],
      state: cloneDirtyLocalState(key),
    };
  });
  return preserved;
};

const restorePreservedDirtyRows = preserved => {
  if (!preserved || !Object.keys(preserved).length) return;
  const nextBaseline = { ...rowDirtyBaseline.value };
  Object.entries(preserved).forEach(([key, entry]) => {
    applyDirtyLocalState(key, entry.state);
    if (entry.baseline != null) nextBaseline[key] = entry.baseline;
  });
  rowDirtyBaseline.value = nextBaseline;
};

const isRowDirty = key => {
  const baseline = rowDirtyBaseline.value?.[key];
  if (baseline == null) return false;
  return baseline !== serializeRowSnapshot(key);
};

const applyButtonVariant = key => (isRowDirty(key) ? 'primary' : 'primary-line');

const hasDirtyRows = () => rowDirtyKeys.some(key => isRowDirty(key));

const isEvalRoleEnabled = (config, role) => {
  const direct = config?.[role];
  if (direct === 'Y' || direct === true) return true;
  if (direct === 'N' || direct === false) return false;
  const yn = config?.[`${role}Yn`] ?? config?.[`${role}EvalYn`];
  return yn === 'Y' || yn === true;
};

const isEnabledYn = value => value === true || String(value ?? '').trim() === 'Y';

const resolveModalEvalGrpId = () => {
  const propGrpId = String(props.evalGrpId ?? '').trim();
  if (propGrpId) return propGrpId;
  const firstRow = (props.evalTargetRows || []).find(row => String(row?.evalGrpId ?? '').trim());
  return String(firstRow?.evalGrpId ?? '').trim();
};

const findModalEvalGroup = () => {
  const grpId = resolveModalEvalGrpId();
  if (!grpId) return null;
  return (evalData.evalGrpList || []).find(g => String(g?.evalGrpId ?? '') === grpId) || null;
};

const hasEvaluatorSection = computed(() => {
  const c = evalConfigLocal.value || {};
  return c.fir === 'Y' || c.sec === 'Y' || c.thr === 'Y' || (isSingleTarget.value && (isEvalRoleEnabled(c, 'mate') || isEvalRoleEnabled(c, 'jnr')));
});

const step07RatioColumnFlags = computed(() => {
  const group = findModalEvalGroup();
  const fromStore = getStep07RatioColumnFlags(
    {
      baseData: {
        performExecYn: typeof props.performExecYn === 'boolean' ? props.performExecYn : evalBaseData?.baseData?.performExecYn,
        abilityExecYn: typeof props.abilityExecYn === 'boolean' ? props.abilityExecYn : evalBaseData?.baseData?.abilityExecYn,
      },
    },
    group,
  );
  return fromStore;
});

const showPerformSection = computed(() => step07RatioColumnFlags.value.perf);

const showCapaSection = computed(() => step07RatioColumnFlags.value.cmpt);

const performTypeFlags = computed(() => getStep07PerformTypeFlags(findModalEvalGroup() || performEval.value));

const showResultRateRow = computed(() => {
  return performTypeFlags.value.result;
});

const showProcessRateRow = computed(() => {
  return performTypeFlags.value.process;
});

const hasPerformEvalType = computed(() => showResultRateRow.value || showProcessRateRow.value);

const step03SelectedCapaGbnIds = computed(() => {
  const list = Array.isArray(evalData.evalList) ? evalData.evalList : [];
  if (!list.length) return null;
  const ids = new Set();
  list.forEach(card => {
    const values = Array.isArray(card?.capaGbnDatas)
      ? card.capaGbnDatas
      : String(card?.capaGbnId ?? '')
          .split(',')
          .map(v => v.trim())
          .filter(Boolean);
    values.forEach(id => ids.add(String(id)));
  });
  return [...ids];
});

const hasCapaGbnEditable = computed(() => {
  const step03Ids = step03SelectedCapaGbnIds.value;
  if (Array.isArray(step03Ids)) return step03Ids.length > 0;
  return (capaGbnEditable.value || []).length > 0;
});

const isResultMdlStmt = computed(() => String((evalConfigLocal.value || {}).resultEvalMdlCd || '') === '005');

const isProcessMdlStmt = computed(() => String((evalConfigLocal.value || {}).processEvalMdlCd || '') === '005');

const performStmtOptions = computed(() => {
  const list = (performTmpltList.value || []).length ? performTmpltList.value : (evalData.performTmpltList || []);
  const opts = list.map(t => ({
    value: String(t.perfStmtId ?? t.resultPerfStmtId ?? t.codeId ?? ''),
    label: String(t.perfStmtNm ?? t.stmtNm ?? t.codeNm ?? t.perfStmtId ?? ''),
  }));
  const map = new Map();
  opts
    .filter(o => o.value)
    .forEach(o => {
      map.set(o.value, o);
    });
  return [{ value: '', label: t('hrEvaluation.conf.basis.common.selectColumn') }, ...map.values()];
});

const evaluatorRows = computed(() => {
  const c = evalConfigLocal.value || {};
  const u = evalUsersLocal.value || {};
  const rows = [];
  if (c.fir === 'Y') {
    rows.push({
      role: 'fir',
      label: t('hrEvaluation.conf.targets.firSuperiorEval'),
      userId: String(u.firEvalUserId ?? ''),
      userNm: String(u.firEvalUserNm ?? ''),
    });
  }
  if (c.sec === 'Y') {
    rows.push({
      role: 'sec',
      label: t('hrEvaluation.conf.targets.secSuperiorEval'),
      userId: String(u.secEvalUserId ?? ''),
      userNm: String(u.secEvalUserNm ?? ''),
    });
  }
  if (c.thr === 'Y') {
    rows.push({
      role: 'thr',
      label: t('hrEvaluation.conf.targets.thrSuperiorEval'),
      userId: String(u.thrEvalUserId ?? ''),
      userNm: String(u.thrEvalUserNm ?? ''),
    });
  }
  if (isSingleTarget.value && isEvalRoleEnabled(c, 'mate')) {
    rows.push({
      role: 'mate',
      label: t('hrEvaluation.conf.targets.mate'),
      multiple: true,
      users: multiEvalUsersLocal.value.mate || [],
    });
  }
  if (isSingleTarget.value && isEvalRoleEnabled(c, 'jnr')) {
    rows.push({
      role: 'jnr',
      label: t('hrEvaluation.conf.targets.jnrUpward'),
      multiple: true,
      users: multiEvalUsersLocal.value.jnr || [],
    });
  }
  return rows;
});

// ===== UI 개선용: 스위치 모델 + 합계 인디케이터 + 아코디언 =====
const canUsePerformSwitch = computed(() => showPerformSection.value && hasPerformEvalType.value);

const canUseCapaSwitch = computed(() => showCapaSection.value && hasCapaGbnEditable.value);

const performYnSwitch = computed({
  get: () => canUsePerformSwitch.value && (evalTargetLocal.value?.allPerformYn ?? 'Y') !== 'N',
  set: v => {
    if (!canUsePerformSwitch.value) return;
    evalTargetLocal.value = { ...evalTargetLocal.value, allPerformYn: v ? 'Y' : 'N' };
  },
});
const capaYnSwitch = computed({
  get: () => canUseCapaSwitch.value && (evalTargetLocal.value?.allCapaYn ?? 'Y') !== 'N',
  set: v => {
    if (!canUseCapaSwitch.value) return;
    evalTargetLocal.value = { ...evalTargetLocal.value, allCapaYn: v ? 'Y' : 'N' };
  },
});

// 성과평가 합계(결과+과정) — 비율 입력이 있는 경우만 계산
const parseNum = v => {
  const n = parseFloat(String(v ?? '').replace(/,/g, ''));
  return Number.isFinite(n) ? n : 0;
};
const performSumPct = computed(() => {
  let sum = 0;
  if (showResultRateRow.value) sum += parseNum(performRateLocal.value.resultRate);
  if (showProcessRateRow.value) sum += parseNum(performRateLocal.value.processRate);
  return sum;
});
const showPerformSumIndicator = computed(() => {
  if (!performYnSwitch.value) return false;
  const hasResult = showResultRateRow.value;
  const hasProcess = showProcessRateRow.value;
  return hasResult || hasProcess;
});
const isPerformSumValid = computed(() => Math.abs(performSumPct.value - 100) < 0.001);

// 역량군 아코디언 펼침 상태 (기본 전부 펼침)
const expandedCapaGroupIds = ref([]);
const isCapaGroupExpanded = id => expandedCapaGroupIds.value.includes(String(id));
const toggleCapaGroup = id => {
  const key = String(id);
  const idx = expandedCapaGroupIds.value.indexOf(key);
  if (idx > -1) expandedCapaGroupIds.value.splice(idx, 1);
  else expandedCapaGroupIds.value.push(key);
};

const isManagerUnset = () => {
  if (!isSingleTarget.value) return false;
  return !String(managerLocal.value.userId ?? '').trim();
};

const handleBeforeClose = async () => {
  if (isManagerUnset() && !hasDirtyRows()) {
    const ok = await confirmAsync(t('hrEvaluation.conf.basis.common.confirm'), st('m_1ad720a63d'));
    if (!ok) return false;
  }
  if (!hasDirtyRows()) return true;
  return await confirmAsync(t('hrEvaluation.conf.basis.common.confirm'), st('m_895e1f26a5'));
};

const requestClose = async () => {
  const canClose = await handleBeforeClose();
  if (!canClose) return;
  rowDirtyBaseline.value = {};
  modalRef.value?.captureInitialTextValues?.();
  emit('update:modelValue', false);
  emit('close');
};

const evalTargetRowsForSave = (appliedKey = null) =>
  effectiveEvalTargetRows.value.map((row, idx) => {
    if (!isSingleTarget.value || applyAllTargets.value || idx !== 0) return row;
    // 부서장 적용 시에만 로컬 값 사용. 그 외는 baseline(서버 값) 유지
    if (appliedKey && appliedKey !== 'manager') {
      const base = parseRowBaseline('manager');
      return {
        ...row,
        managerId: base?.userId ?? row.managerId,
        managerNm: base?.userNm ?? row.managerNm,
      };
    }
    return {
      ...row,
      managerId: managerLocal.value.userId,
      managerNm: managerLocal.value.userNm,
    };
  });

const parseRowBaseline = key => {
  const raw = rowDirtyBaseline.value?.[key];
  if (raw == null) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

/** appliedKey에 해당하는 행만 로컬(미저장) 값 사용, 나머지는 baseline 값 사용 */
const useLocalForSaveKey = (appliedKey, key) => !appliedKey || appliedKey === key;

const resolveSingleEvalUserId = (role, appliedKey) => {
  if (useLocalForSaveKey(appliedKey, role)) {
    return String((evalUsersLocal.value || {})[`${role}EvalUserId`] ?? '');
  }
  const base = parseRowBaseline(role);
  if (base && typeof base === 'object') return String(base.userId ?? '');
  return String((evalUsersLocal.value || {})[`${role}EvalUserId`] ?? '');
};

const resolveMultiEvalUserIds = (role, appliedKey) => {
  let users;
  if (useLocalForSaveKey(appliedKey, role)) {
    users = multiEvalUsersLocal.value?.[role] || [];
  } else {
    const base = parseRowBaseline(role);
    users = Array.isArray(base) ? base : multiEvalUsersLocal.value?.[role] || [];
  }
  return (users || [])
    .map(user => String(user.userId ?? ''))
    .filter(Boolean)
    .join(',');
};

const resolveManagerIdForSave = appliedKey => {
  if (useLocalForSaveKey(appliedKey, 'manager')) {
    return String(managerLocal.value.userId ?? '');
  }
  const base = parseRowBaseline('manager');
  if (base && typeof base === 'object') return String(base.userId ?? '');
  return String(managerLocal.value.userId ?? '');
};

const resolveManagerNmForSave = appliedKey => {
  if (useLocalForSaveKey(appliedKey, 'manager')) {
    return String(managerLocal.value.userNm ?? '');
  }
  const base = parseRowBaseline('manager');
  if (base && typeof base === 'object') return String(base.userNm ?? '');
  return String(managerLocal.value.userNm ?? '');
};

const appendCommonFields = (fd, appliedKey = null) => {
  const commonYear = String(useCommonStore().findYear ?? '');
  const y = String(props.findYear ?? '') || commonYear;
  // 레거시/JPA 등에서 YEAR 컬럼에 매핑되는 파라미터 (findYear만으로는 부족한 서버 대비)
  fd.append('year', y);
  fd.append('findYear', y);
  fd.append('evalCycleCd', String(props.evalCycleCd ?? ''));
  const rootGrp = String(props.evalGrpId ?? '').trim();
  if (rootGrp) {
    fd.append('evalGrpId', rootGrp);
  }
  if (isSingleTarget.value) {
    fd.append('evalTargetId', String(props.evalTargetId ?? ''));
    fd.append('managerId', resolveManagerIdForSave(appliedKey));
    fd.append('managerNm', resolveManagerNmForSave(appliedKey));
  }
  evalTargetRowsForSave(appliedKey).forEach((row, idx) => {
    fd.append(`evalTargetList[${idx}].evalGrpId`, String(row.evalGrpId ?? ''));
    fd.append(`evalTargetList[${idx}].evalTargetId`, String(row.evalTargetId ?? ''));
    fd.append(`evalTargetList[${idx}].managerId`, String(row.managerId ?? ''));
    fd.append(`evalTargetList[${idx}].managerNm`, String(row.managerNm ?? ''));
  });
};

/**
 * hreConfMngList.jsp saveEvalTargetConfAll 과 동일 필드 → HreTargetMngVO / saveEvalTargetAllForAdmin
 * (performProcessRate·performResultRate, capaGbnIds|capaGbnRates, capaItemIds 는 파이프 구분)
 * appliedKey: 사용자가 적용을 누른 행만 로컬 변경값을 전송하고, 나머지는 baseline(서버 값)을 전송
 */
const appendSaveEvalTargetAllAlignedFields = (fd, appliedKey = null) => {
  const cfg = evalConfigLocal.value || {};
  const performBase = useLocalForSaveKey(appliedKey, 'perform') ? null : parseRowBaseline('perform');
  const capaBase = useLocalForSaveKey(appliedKey, 'capa') ? null : parseRowBaseline('capa');

  fd.append('firEvalUserId', resolveSingleEvalUserId('fir', appliedKey));
  fd.append('secEvalUserId', resolveSingleEvalUserId('sec', appliedKey));
  fd.append('thrEvalUserId', resolveSingleEvalUserId('thr', appliedKey));
  fd.append('mateEvalUserIds', resolveMultiEvalUserIds('mate', appliedKey));
  fd.append('jnrEvalUserIds', resolveMultiEvalUserIds('jnr', appliedKey));

  fd.append('processEvalMdlCd', String(cfg.processEvalMdlCd ?? ''));
  fd.append('resultEvalMdlCd', String(cfg.resultEvalMdlCd ?? ''));

  const performYnLocal =
    String(cfg.performEvalYn) === 'Y' && canUsePerformSwitch.value ? (performYnSwitch.value ? 'Y' : 'N') : 'N';
  const capaYnLocal = String(cfg.capaEvalYn) === 'Y' && canUseCapaSwitch.value ? (capaYnSwitch.value ? 'Y' : 'N') : 'N';

  if (performBase) {
    const yn = String(performBase.allPerformYn ?? '');
    if (yn === 'Y' || yn === 'N') {
      fd.append('performYn', String(cfg.performEvalYn) === 'Y' && canUsePerformSwitch.value ? yn : 'N');
    } else {
      fd.append('performYn', performYnLocal);
    }
  } else {
    fd.append('performYn', performYnLocal);
  }

  if (capaBase) {
    const yn = String(capaBase.allCapaYn ?? '');
    if (yn === 'Y' || yn === 'N') {
      fd.append('capaYn', String(cfg.capaEvalYn) === 'Y' && canUseCapaSwitch.value ? yn : 'N');
    } else {
      fd.append('capaYn', capaYnLocal);
    }
  } else {
    fd.append('capaYn', capaYnLocal);
  }

  const resultPerfStmtId = performBase ? String(performBase.resultPerfStmtId ?? '') : String(cfg.resultPerfStmtId ?? '');
  const processPerfStmtId = performBase ? String(performBase.processPerfStmtId ?? '') : String(cfg.processPerfStmtId ?? '');
  if (isProcessMdlStmt.value) {
    fd.append('processPerfStmtId', processPerfStmtId);
  }
  if (isResultMdlStmt.value) {
    fd.append('resultPerfStmtId', resultPerfStmtId);
  }

  // performEval 이 비어 있어도 화면(showResultRateRow)과 동일 조건으로 performProcessRate·performResultRate 전송.
  if (showProcessRateRow.value) {
    fd.append(
      'performProcessRate',
      performBase ? String(performBase.processRate ?? '0') : String(performRateLocal.value.processRate ?? '0'),
    );
  }
  if (showResultRateRow.value) {
    fd.append(
      'performResultRate',
      performBase ? String(performBase.resultRate ?? '0') : String(performRateLocal.value.resultRate ?? '0'),
    );
  }

  if (capaBase) {
    const gbnList = Array.isArray(capaBase.gbnList) ? capaBase.gbnList : [];
    fd.append('capaGbnIds', gbnList.map(g => String(g.capaGbnId ?? '')).join('|'));
    fd.append('capaGbnRates', gbnList.map(g => String(g.capaGbnRate ?? '0')).join('|'));
    const itemIds = Array.isArray(capaBase.itemIds) ? capaBase.itemIds : [];
    fd.append('capaItemIds', itemIds.map(id => String(id ?? '')).filter(Boolean).join('|'));
  } else {
    const gbnList = capaGbnEditable.value || [];
    fd.append('capaGbnIds', gbnList.map(g => String(g.capaGbnId ?? '')).join('|'));
    fd.append('capaGbnRates', gbnList.map(g => String(g.capaGbnRate ?? '0')).join('|'));
    const itemIds = effectiveCapaItemList.value.map(it => String(it.capaItemId ?? '')).filter(Boolean);
    fd.append('capaItemIds', itemIds.join('|'));
  }
};

const openUserPicker = role => {
  userPickRole.value = role;
  isUserSelectOpen.value = true;
};

/** userList_json / userInsaList_json 등 응답 필드명 차이 흡수 */
const pickEvalUserId = u => {
  if (!u || typeof u !== 'object') return '';
  return String(
    u.userId ??
      u.USER_ID ??
      u.user_id ??
      u.empId ??
      u.EMP_ID ??
      u.emp_id ??
      u.loginId ??
      u.LOGIN_ID ??
      u.id ??
      u.empNo ??
      u.EMP_NO ??
      '',
  ).trim();
};

const pickEvalUserNm = u => {
  if (!u || typeof u !== 'object') return '';
  const nm =
    u.userNm ??
    u.USER_NM ??
    u.user_nm ??
    u.empNm ??
    u.EMP_NM ??
    u.emp_nm ??
    u.korNm ??
    u.name ??
    u.userName;
  return String(nm ?? '').trim();
};

const makePickedUser = u => {
  const userId = pickEvalUserId(u);
  const userNm = pickEvalUserNm(u) || userId;
  if (!userId && !userNm) return null;
  return {
    ...u,
    id: userId,
    userId,
    userNm,
    name: userNm,
    label: userNm,
  };
};

const splitUserValue = value =>
  String(value ?? '')
    .split(/[|,]/)
    .map(v => v.trim())
    .filter(Boolean);

/** GROUP_CONCAT(... SEPARATOR ' ') → mateEvalUserNm / MATE_EVAL_USER_NM 등: 공백 구분 (mateEvalUserNmList 등 콤마 목록은 제외) */
const splitUserNameListByWhitespace = value =>
  String(value ?? '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);

const normNameFieldKey = key =>
  String(key || '')
    .replace(/_/g, '')
    .toLowerCase();

const isWhitespaceSplitNameFieldKey = key => {
  const k = normNameFieldKey(key);
  if (k.includes('nmlist')) return false;
  if (k.includes('userid') || k.includes('evaluserid')) return false;
  return (
    k === 'mateevalusernm' ||
    k === 'mateevalusernms' ||
    k === 'evalusernmmate' ||
    k === 'jnrevalusernm' ||
    k === 'jnrevalusernms' ||
    k === 'evalusernmjnr'
  );
};

/** 어떤 키로 값이 채워졌는지 함께 반환 (이름 필드별 split 방식 분기용) */
const firstFieldEntry = (source, keys) => {
  if (!source || typeof source !== 'object') return { key: '', value: '' };
  for (const key of keys) {
    const direct = source[key];
    const value = !isBlankValue(direct) ? direct : getObjectField(source, key);
    if (!isBlankValue(value)) return { key, value };
  }
  return { key: '', value: '' };
};

const isBlankValue = value => value == null || String(value).trim() === '';

const firstNonBlank = (...values) => values.find(value => !isBlankValue(value));

const getObjectField = (source, fieldName) => {
  if (!source || !fieldName || typeof source !== 'object') return '';
  if (!isBlankValue(source[fieldName])) return source[fieldName];
  const lower = String(fieldName).toLowerCase();
  const key = Object.keys(source).find(k => k.toLowerCase() === lower);
  return key ? source[key] : '';
};

const buildUserListFromFields = (source, idKeys, nameKeys) => {
  const idEntry = firstFieldEntry(source, idKeys);
  const ids = splitUserValue(idEntry.value);

  const nameEntry = firstFieldEntry(source, nameKeys);
  const rawNames = nameEntry.value;
  const names = isWhitespaceSplitNameFieldKey(nameEntry.key) ? splitUserNameListByWhitespace(rawNames) : splitUserValue(rawNames);

  if (!ids.length && names.length) {
    return names.map((name, idx) => ({ id: `display-${idx}`, userId: '', userNm: name, name, label: name }));
  }

  return ids
    .map((id, idx) => {
      const name = names[idx] || id;
      return { id, userId: id, userNm: name, name, label: name };
    })
    .filter(user => user.userId);
};

const uniqueUsers = users => {
  const map = new Map();
  (users || []).forEach(user => {
    const picked = makePickedUser(user);
    if (picked?.userId) map.set(picked.userId, picked);
  });
  return [...map.values()];
};

const onUserSelectConfirm = (user, roleHint) => {
  let u = unref(user);

  if (!u && selectedUserObjectFromStore.value) {
    u = selectedUserObjectFromStore.value;
  }

  const r = roleHint || userPickRole.value;

  if (!u || !r) {
    return;
  }

  if (multiEvalRoles.includes(r)) {
    const selected = Array.isArray(u) ? u : [u];
    multiEvalUsersLocal.value = {
      ...multiEvalUsersLocal.value,
      [r]: uniqueUsers(selected),
    };
    userPickRole.value = '';
    return;
  }

  const picked = makePickedUser(u);
  const uid = picked?.userId || '';
  const unm = picked?.userNm || uid;

  const next = { ...evalUsersLocal.value };
  if (r === 'manager') {
    managerLocal.value = { userId: uid, userNm: unm };
  } else if (r === 'fir') {
    next.firEvalUserId = uid;
    next.firEvalUserNm = unm;
  } else if (r === 'sec') {
    next.secEvalUserId = uid;
    next.secEvalUserNm = unm;
  } else if (r === 'thr') {
    next.thrEvalUserId = uid;
    next.thrEvalUserNm = unm;
  }
  evalUsersLocal.value = next;
  userPickRole.value = '';
};

const clearEvalUser = role => {
  if (role === 'manager') {
    managerLocal.value = { userId: '', userNm: '' };
    return;
  }
  if (multiEvalRoles.includes(role)) {
    multiEvalUsersLocal.value = {
      ...multiEvalUsersLocal.value,
      [role]: [],
    };
    return;
  }

  const next = { ...evalUsersLocal.value };
  if (role === 'fir') {
    next.firEvalUserId = '';
    next.firEvalUserNm = '';
  } else if (role === 'sec') {
    next.secEvalUserId = '';
    next.secEvalUserNm = '';
  } else if (role === 'thr') {
    next.thrEvalUserId = '';
    next.thrEvalUserNm = '';
  }
  evalUsersLocal.value = next;
};

const removeMultiEvalUser = (role, userId) => {
  if (!multiEvalRoles.includes(role)) return;
  multiEvalUsersLocal.value = {
    ...multiEvalUsersLocal.value,
    [role]: (multiEvalUsersLocal.value?.[role] || []).filter(user => String(user.userId || user.userNm) !== String(userId)),
  };
};

const validatePerformRates = () => {
  if (!performYnSwitch.value) return true;
  if (!showResultRateRow.value && !showProcessRateRow.value) return true;

  let numericRows = 0;
  let sum = 0;
  if (showResultRateRow.value) {
    numericRows += 1;
    sum += parseNum(performRateLocal.value.resultRate);
  }
  if (showProcessRateRow.value) {
    numericRows += 1;
    sum += parseNum(performRateLocal.value.processRate);
  }
  if (numericRows === 0) return true;
  if (Math.abs(sum - 100) > 0.001) {
    showAlert(
      t('hrEvaluation.conf.basis.common.notice'),
      numericRows >= 2 ? st('m_6d20cd93a1') : st('m_535c6aa044'),
    );
    return false;
  }
  return true;
};

const handleSaveResponse = async (res, appliedKey = null) => {
  const msg = res?.msg || res?.message || st('m_0c47ff0995');
  const fail =
    res?.result === 'FAIL' ||
    res?.result === 'ERROR' ||
    res?.resultCode === 'FAIL' ||
    res?.resultCode === 'ERROR';
  if (fail) {
    showAlert(t('hrEvaluation.conf.basis.common.notice'), msg);
    return false;
  }
  // 부분 적용 후 reload 해도 다른 dirty 행의 미적용 값은/적용 버튼 상태가 유지되도록 보존
  const preservedDirty = appliedKey ? capturePreservedDirtyRows(appliedKey) : null;
  showAlert(t('hrEvaluation.conf.basis.common.alert'), msg);
  emit('success');
  await load(true);
  await nextTick();
  if (preservedDirty) {
    restorePreservedDirtyRows(preservedDirty);
    await nextTick();
  }
  modalRef.value?.captureInitialTextValues?.();
  return true;
};

const onApplyEvalUser = async role => {
  if (!canSave.value) {
    showAlert(t('hrEvaluation.conf.basis.common.notice'), st('m_7b109d197e'));
    return;
  }

  if (role === 'manager') {
    const mid = String(managerLocal.value.userId ?? '').trim();
    const mnm = String(managerLocal.value.userNm ?? '').trim();
    if (mnm && !mid) {
      showAlert(t('hrEvaluation.conf.basis.common.notice'), st('m_f9f8016a62'));
      return;
    }
  } else if (['fir', 'sec', 'thr'].includes(role)) {
    const uid = String((evalUsersLocal.value || {})[`${role}EvalUserId`] ?? '').trim();
    const unm = String((evalUsersLocal.value || {})[`${role}EvalUserNm`] ?? '').trim();
    if (unm && !uid) {
      showAlert(t('hrEvaluation.conf.basis.common.notice'), st('m_6df2cdf7de'));
      return;
    }
  }

  if (applyAllTargets.value) {
    const confirmed = await confirmAsync(t('hrEvaluation.conf.basis.common.confirm'), st('m_5eb9d56179'));
    if (!confirmed) return;
  }

  isLoading.value = true;

  const fd = new FormData();
  appendCommonFields(fd, role);
  appendSaveEvalTargetAllAlignedFields(fd, role);
  fd.append('saveGbn', role === 'manager' ? 'manager' : 'evalUser');
  fd.append('saveEvalGbn', role);
  try {
    const res = await saveEvalTargetAllForAdmin(fd);
    await handleSaveResponse(res, role);
  } catch (e) {
    console.error(e);
  } finally {
    isLoading.value = false;
  }
};

const onApplyPerform = async () => {
  if (!canSave.value) {
    showAlert(t('hrEvaluation.conf.basis.common.notice'), st('m_7b109d197e'));
    return;
  }
  if (!canUsePerformSwitch.value) {
    showAlert(t('hrEvaluation.conf.basis.common.notice'), st('m_a1bb2fd8e3'));
    return;
  }
  if (applyAllTargets.value) {
    const confirmed = await confirmAsync(t('hrEvaluation.conf.basis.common.confirm'), st('m_5eb9d56179'));
    if (!confirmed) return;
  }
  if (!validatePerformRates()) return;

  isLoading.value = true;

  const fd = new FormData();
  appendCommonFields(fd, 'perform');
  appendSaveEvalTargetAllAlignedFields(fd, 'perform');
  fd.append('saveGbn', 'perform');
  try {
    const res = await saveEvalTargetAllForAdmin(fd);
    await handleSaveResponse(res, 'perform');
  } catch (e) {
    console.error(e);
  } finally {
    isLoading.value = false;
  }
};

const onApplyCapa = async () => {
  if (!canSave.value) {
    showAlert(t('hrEvaluation.conf.basis.common.notice'), st('m_7b109d197e'));
    return;
  }
  if (!canUseCapaSwitch.value) {
    showAlert(t('hrEvaluation.conf.basis.common.notice'), st('m_e8789704b2'));
    return;
  }
  if (applyAllTargets.value) {
    const confirmed = await confirmAsync(t('hrEvaluation.conf.basis.common.confirm'), st('m_5eb9d56179'));
    if (!confirmed) return;
  }

  isLoading.value = true;

  const fd = new FormData();
  appendCommonFields(fd, 'capa');
  appendSaveEvalTargetAllAlignedFields(fd, 'capa');
  fd.append('saveGbn', 'ability');
  try {
    const res = await saveEvalTargetAllForAdmin(fd);
    await handleSaveResponse(res, 'capa');
  } catch (e) {
    console.error(e);
  } finally {
    isLoading.value = false;
  }
};

const capaItemListSnapshot = ref([]);

/** step3 역량군(capaGbnEditable)에 매핑되는 항목만 UI·저장에 사용 (capaGbnId 누락·불일치 고아 제외) */
const capaGbnIdSetFromList = gbnList =>
  new Set((gbnList || []).map(g => String(g.capaGbnId ?? '')).filter(Boolean));

const filterCapaItemsByEditableGbns = (items, gbnList = capaGbnEditable.value) => {
  const allowed = capaGbnIdSetFromList(gbnList);
  if (!allowed.size) return [];
  return (items || []).filter(it => allowed.has(String(it.capaGbnId ?? '')));
};

const effectiveCapaItemList = computed(() => filterCapaItemsByEditableGbns(capaItemListSnapshot.value));

const isCapaItemPickerOpen = ref(false);
const capaItemPickerRow = ref(null);

const capaItemPreselectedForPicker = computed(() => {
  const row = capaItemPickerRow.value;
  if (!row) return [];
  const gid = String(row.capaGbnId ?? '');
  return effectiveCapaItemList.value.filter(it => String(it.capaGbnId ?? '') === gid);
});

const capaItemFallbackForPicker = computed(() => capaItemPreselectedForPicker.value);

const openCapaItemPicker = g => {
  capaItemPickerRow.value = g;
  isCapaItemPickerOpen.value = true;
};

const onCapaItemPickerConfirm = items => {
  const row = capaItemPickerRow.value;
  if (!row) return;
  const gid = String(row.capaGbnId ?? '');
  const rest = effectiveCapaItemList.value.filter(it => String(it.capaGbnId ?? '') !== gid);
  const enriched = (items || []).map(it => ({
    ...it,
    capaGbnNm: String(row.capaGbnNm ?? ''),
    weightUseYn: String(row.weightUseYn ?? ''),
  }));
  capaItemListSnapshot.value = [...rest, ...enriched];
  capaItemPickerRow.value = null;
};

const capaSnapshotByGbnId = computed(() => {
  const map = {};
  for (const it of effectiveCapaItemList.value) {
    const gid = String(it.capaGbnId ?? '');
    if (!map[gid]) map[gid] = [];
    map[gid].push(it);
  }
  return map;
});

const capaSnapshotRowsForGbn = g => capaSnapshotByGbnId.value[String(g.capaGbnId ?? '')] || [];

const capaSnapshotRowKey = (it, idx) =>
  `${String(it.capaGbnId ?? '')}_${String(it.capaItemId ?? '')}_${String(it.cmptDictId ?? '')}_${idx}`;

const labelForCapaSnapshotItem = it => {
  const g = (capaGbnEditable.value || []).find(x => String(x.capaGbnId ?? '') === String(it.capaGbnId ?? ''));
  const wYn = String(it.weightUseYn ?? g?.weightUseYn ?? '') === 'Y';
  const nm = String(it.capaItemNm ?? it.capaItemId ?? '');
  const w = it.weight != null && it.weight !== '' ? String(it.weight) : '';
  if (wYn && w) return `${nm} ( ${w}% )`;
  return nm;
};

const applyPayload = (adminRes, statusRes) => {
  const cfg = adminRes?.evalConfig ? { ...adminRes.evalConfig } : {};
  const eb = adminRes?.evalBase || {};
  const statusEvalConfig = statusRes?.evalConfig || {};
  const localGroup = findModalEvalGroup();
  const evalList = Array.isArray(statusRes?.evalList) ? statusRes.evalList : Array.isArray(adminRes?.evalList) ? adminRes.evalList : [];
  const statusTarget = (statusRes?.targetStatusList || []).find(
    row => String(row?.evalTargetId ?? row?.id ?? '') === String(props.evalTargetId ?? ''),
  );

  if (localGroup) {
    cfg.performEvalYn = isEnabledYn(localGroup.performEvalYn) ? 'Y' : 'N';
    cfg.resultEvalMdlCd = String(localGroup.resultEvalMdlCd ?? cfg.resultEvalMdlCd ?? '');
    cfg.processEvalMdlCd = String(localGroup.processEvalMdlCd ?? cfg.processEvalMdlCd ?? '');
    cfg.resultPerfStmtId = String(localGroup.resultPerfStmtId ?? cfg.resultPerfStmtId ?? '');
    cfg.processPerfStmtId = String(localGroup.processPerfStmtId ?? cfg.processPerfStmtId ?? '');
  }

  if (isBlankValue(cfg.resultEvalMdlCd)) {
    cfg.resultEvalMdlCd = String(statusEvalConfig.resultEvalMdlCd ?? '');
  }
  if (isBlankValue(cfg.processEvalMdlCd)) {
    cfg.processEvalMdlCd = String(statusEvalConfig.processEvalMdlCd ?? '');
  }

  // hreTargetMng/evalTargetConfigForAdmin: performEvalYn·capaEvalYn 이 "" 이고 evalBase 로만 시행 여부가 오는 경우
  if (!String(cfg.performEvalYn ?? '').trim()) {
    const x = String(eb.performExecYn ?? '').trim();
    if (x === 'Y' || x === 'N') cfg.performEvalYn = x;
  }
  if (!String(cfg.capaEvalYn ?? '').trim()) {
    const x = String(eb.abilityExecYn ?? '').trim();
    if (x === 'Y' || x === 'N') cfg.capaEvalYn = x;
  }

  evalList.forEach(item => {
    const evalCd = String(item?.evalCd ?? '').trim();
    if (['fir', 'sec', 'thr', 'mate', 'jnr'].includes(evalCd)) {
      cfg[evalCd] = 'Y';
    }
  });

  const resultPerfStmtId =
    getObjectField(statusTarget, 'RESULT_PERF_STMT_ID') ||
    getObjectField(statusTarget, 'resultPerfStmtId') ||
    getObjectField(adminRes?.evalTarget, 'resultPerfStmtId') ||
    getObjectField(adminRes?.evalConfig, 'resultPerfStmtId');
  const resultPerfStmtNm =
    getObjectField(statusTarget, 'RESULT_PERF_STMT_NM') ||
    getObjectField(statusTarget, 'resultPerfStmtNm') ||
    getObjectField(adminRes?.evalTarget, 'resultPerfStmtNm') ||
    getObjectField(adminRes?.evalConfig, 'resultPerfStmtNm');
  const processPerfStmtId =
    getObjectField(statusTarget, 'PROCESS_PERF_STMT_ID') ||
    getObjectField(statusTarget, 'processPerfStmtId') ||
    getObjectField(adminRes?.evalTarget, 'processPerfStmtId') ||
    getObjectField(adminRes?.evalConfig, 'processPerfStmtId');
  const processPerfStmtNm =
    getObjectField(statusTarget, 'PROCESS_PERF_STMT_NM') ||
    getObjectField(statusTarget, 'processPerfStmtNm') ||
    getObjectField(adminRes?.evalTarget, 'processPerfStmtNm') ||
    getObjectField(adminRes?.evalConfig, 'processPerfStmtNm');

  if (String(cfg.resultEvalMdlCd) === '005' && isBlankValue(cfg.resultPerfStmtId) && !isBlankValue(resultPerfStmtId)) {
    cfg.resultPerfStmtId = String(resultPerfStmtId);
  }
  if (String(cfg.processEvalMdlCd) === '005' && isBlankValue(cfg.processPerfStmtId) && !isBlankValue(processPerfStmtId)) {
    cfg.processPerfStmtId = String(processPerfStmtId);
  }

  evalConfigLocal.value = cfg;

  const eu = adminRes?.evalUsers && typeof adminRes.evalUsers === 'object' ? { ...adminRes.evalUsers } : {};
  evalUsersLocal.value = {
    firEvalUserId: String(eu.firEvalUserId ?? ''),
    firEvalUserNm: String(eu.firEvalUserNm ?? ''),
    secEvalUserId: String(eu.secEvalUserId ?? ''),
    secEvalUserNm: String(eu.secEvalUserNm ?? ''),
    thrEvalUserId: String(eu.thrEvalUserId ?? ''),
    thrEvalUserNm: String(eu.thrEvalUserNm ?? ''),
  };

  const et = adminRes?.evalTarget || {};
  const firstRow = (props.evalTargetRows || [])[0] || {};
  managerLocal.value = {
    userId: String(
      firstNonBlank(et.managerId, et.chiefId, statusTarget?.managerId, statusTarget?.chiefId, firstRow.managerId, '') ?? '',
    ),
    userNm: String(
      firstNonBlank(et.managerNm, et.chiefNm, statusTarget?.managerNm, statusTarget?.chiefNm, firstRow.managerNm, '') ?? '',
    ),
  };
  const multiEvalSource = { ...statusTarget, ...eu };
  const mateUsers = buildUserListFromFields(
    multiEvalSource,
    ['mateEvalUserIdList', 'mateEvalUserIds', 'mateEvalUserId', 'evaluseridmate', 'evalUserIdmate', 'evalUserIdMate'],
    ['MATE_EVAL_USER_NM', 'mateEvalUserNm', 'mateEvalUserNms', 'mateEvalUserNmList', 'evalusernmmate', 'evalUserNmmate', 'evalUserNmMate'],
  );
  const jnrUsers = buildUserListFromFields(
    multiEvalSource,
    ['jnrEvalUserIdList', 'jnrEvalUserIds', 'jnrEvalUserId', 'evaluseridjnr', 'evalUserIdjnr', 'evalUserIdJnr'],
    ['JNR_EVAL_USER_NM', 'jnrEvalUserNm', 'jnrEvalUserNms', 'jnrEvalUserNmList', 'evalusernmjnr', 'evalUserNmjnr', 'evalUserNmJnr'],
  );
  multiEvalUsersLocal.value = {
    mate: mateUsers,
    jnr: jnrUsers,
  };

  // admin(evalTargetConfigForAdmin)의 performYn/capaYn은 대상자별 값과 불일치할 수 있음 → status 목록 우선
  const performYnFromStatus = getObjectField(statusTarget, 'performYn') || getObjectField(statusTarget, 'PERFORM_YN');
  const capaYnFromStatus = getObjectField(statusTarget, 'capaYn') || getObjectField(statusTarget, 'CAPA_YN');
  const resolvedPerformYn = String(firstNonBlank(performYnFromStatus, et.performYn, et.allPerformYn, eb.performExecYn, 'Y'));
  const resolvedCapaYn = String(firstNonBlank(capaYnFromStatus, et.capaYn, et.allCapaYn, eb.abilityExecYn, 'Y'));
  evalTargetLocal.value = {
    allPerformYn: resolvedPerformYn,
    allCapaYn: resolvedCapaYn,
  };

  const pr = adminRes?.performRate || {};
  const resultRateFromStatus = getObjectField(statusTarget, 'performgbnrateresult');
  const processRateFromStatus = getObjectField(statusTarget, 'performgbnrateprocess');
  const nextResultRate = String(firstNonBlank(pr.resultRate, pr.resultEvalRate, resultRateFromStatus, '0'));
  const nextProcessRate = String(firstNonBlank(pr.processRate, pr.processEvalRate, processRateFromStatus, '0'));
  performRateLocal.value = {
    resultRate: nextResultRate,
    processRate: nextProcessRate,
  };

  const gbnList = (adminRes?.capaGbnList || []).map((row, i) => ({
    ...row,
    capaGbnRate: String(row.capaGbnRate ?? row.rate ?? '0'),
    _idx: i,
  }));
  capaGbnEditable.value = gbnList;
  // 기본적으로 모든 역량군을 펼친 상태로 시작
  expandedCapaGroupIds.value = gbnList.map(g => String(g.capaGbnId ?? ''));

  const rawCapaItems = adminRes?.capaItemList ? [...adminRes.capaItemList] : [];
  capaItemListSnapshot.value = filterCapaItemsByEditableGbns(rawCapaItems, gbnList);

  const rawPe =
    statusRes?.performEval ||
    statusRes?.evalPerform ||
    adminRes?.performEval ||
    adminRes?.evalPerform ||
    {};
  performEval.value = typeof rawPe === 'object' && rawPe !== null ? { ...rawPe } : {};
  if (localGroup) {
    performEval.value = {
      ...performEval.value,
      resultEvalYn: isEnabledYn(localGroup.resultEvalYn) ? 'Y' : 'N',
      processEvalYn: isEnabledYn(localGroup.processEvalYn) ? 'Y' : 'N',
    };
  }

  const tm =
    statusRes?.performTmpltList ||
    statusRes?.performTemplateList ||
    statusRes?.performStmtList ||
    adminRes?.performTmpltList ||
    adminRes?.performTemplateList ||
    adminRes?.performStmtList ||
    [];
  const fallbackStmtList = [
    !isBlankValue(resultPerfStmtId) || !isBlankValue(resultPerfStmtNm)
      ? { perfStmtId: String(resultPerfStmtId || resultPerfStmtNm), perfStmtNm: String(resultPerfStmtNm || resultPerfStmtId) }
      : null,
    !isBlankValue(processPerfStmtId) || !isBlankValue(processPerfStmtNm)
      ? { perfStmtId: String(processPerfStmtId || processPerfStmtNm), perfStmtNm: String(processPerfStmtNm || processPerfStmtId) }
      : null,
  ].filter(Boolean);
  performTmpltList.value = [...(Array.isArray(tm) && tm.length ? tm : (evalData.performTmpltList || [])), ...fallbackStmtList];
  resetAllRowDirty();
};

const load = async (silent = false) => {
  if (!props.findYear || !props.evalCycleCd) return;

  const isBatchAll = !String(props.evalTargetId || '').trim();

  if (!silent) {
    loading.value = true;
    errorMessage.value = '';
  }
  try {
    await initializeEvalTargetSelectionData(true);
    const params = new URLSearchParams();
    params.append('findYear', props.findYear);
    params.append('evalCycleCd', props.evalCycleCd);

    if (isBatchAll) {
      // hreStatusList.jsp loadEvalTargetConfigAll: evalTargetId="" , evalGrpId 미전달(진행현황 일괄·평가군 혼합)
      params.append('evalTargetId', '');
      const grp = String(props.evalGrpId || '').trim();
      if (grp) {
        // 평가설정 Step07 등: 선택 평가군으로 일괄 조회할 때만 evalGrpId 전달
        params.append('evalGrpId', grp);
      }
    } else {
      params.append('evalGrpId', String(props.evalGrpId ?? ''));
      params.append('evalTargetId', String(props.evalTargetId));
    }

    const adminP = fetchEvalTargetConfigForAdmin(params);

    const grpForStatus = String(props.evalGrpId || '').trim();
    const statusP = grpForStatus
      ? selectTargetStatusList(
          new URLSearchParams({
            findYear: props.findYear,
            evalCycleCd: props.evalCycleCd,
            evalGrpId: grpForStatus,
            findUserId: '',
          }),
        )
      : Promise.resolve({});

    const [adminRes, statusRes] = await Promise.all([adminP, statusP]);
    applyPayload(adminRes || {}, statusRes || {});
  } catch (e) {
    console.error(e);
    if (silent) {
      showAlert(t('hrEvaluation.conf.basis.common.error'), st('m_44706f85e8'));
    } else {
      errorMessage.value = st('m_026d44ffed');
      showAlert(t('hrEvaluation.conf.basis.common.error'), errorMessage.value);
    }
  } finally {
    if (!silent) {
      loading.value = false;
    }
  }
};

watch(
  () => props.modelValue,
  v => {
    if (v) load();
  },
);

watch(
  () => props.modelValue,
  v => {
    if (!v) {
      applyAllTargets.value = false;
      rowDirtyBaseline.value = {};
    }
  },
);

watch(
  () => [
    managerLocal.value,
    evalUsersLocal.value,
    multiEvalUsersLocal.value,
    evalTargetLocal.value,
    performRateLocal.value,
    (evalConfigLocal.value || {}).resultPerfStmtId,
    (evalConfigLocal.value || {}).processPerfStmtId,
    capaGbnEditable.value,
    capaItemListSnapshot.value,
  ],
  () => {
    nextTick(() => modalRef.value?.captureInitialTextValues?.());
  },
  { deep: true },
);
</script>

<!--
  너비: UiModal scoped의 .ui-popup--xlarge 규칙이 강해서, 부모 scoped :deep()만으로는 안 먹는 경우가 있음.
  루트(.step07-eval-target-config-modal) → 직계 .ui-popup__content 를 비-scoped로 덮어씀.
-->
<style lang="scss">
.step07-eval-target-config-modal > .ui-popup__content {
  width: min(650px, 92vw) !important;
  max-width: min(650px, 92vw) !important;
  min-width: 0 !important;
  box-sizing: border-box;
}

.step07-eval-target-config-modal > .ui-popup__content.ui-popup--fullscreen {
  width: 100vw !important;
  max-width: 100vw !important;
  min-width: 0 !important;
  height: 100vh !important;
  max-height: 100vh !important;
}

.step07-eval-target-config-modal > .ui-popup__body {
  overflow-x: hidden;
  overflow-y: auto;
}
</style>

<style scoped lang="scss">
/* ── 공통 ── */
.etcm__modal-title {
  display: inline-flex;
  align-items: center;
}
.etcm__footer-row {
  display: flex;
  width: 100%;
  align-items: center;
  gap: 12px;
}

.etcm__footer-close {
  margin-left: auto;
}

.etcm__footer-row :deep(.ui-checkbox) {
  font-size: 16px;
  font-weight: 500;
}

.etcm__footer-row :deep(.ui-checkbox__label) {
  font-size: 16px;
}

.etcm__loading,
.etcm__error {
  padding: 24px;
  text-align: center;
}
.etcm__error {
  color: #c00;
}
.etcm__body {
  max-height: 72vh;
  overflow-y: auto;
  padding: 4px 0;
}

/* ── 카드 ── */
.etcm__card {
  margin-bottom: 14px;
  border: 1px solid #e0e4e8;
  border-radius: 10px;
  background: #ffffff;
  overflow: hidden;
  transition: background 0.15s, opacity 0.15s;

  &:last-of-type {
    margin-bottom: 0;
  }

  &--inactive {
    background: #f9fafb;
  }
}

.etcm__card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 16px;
  background: #f4f6f9;
  border-bottom: 1px solid #e0e4e8;
}

.etcm__card--inactive .etcm__card-header {
  background: #f0f1f3;
}

.etcm__card-title {
  margin: 0;
  font-size: 15px;
  font-weight: 700;
  color: #1f2937;
  display: inline-flex;
  align-items: center;
  gap: 8px;

  &::before {
    content: '';
    width: 4px;
    height: 16px;
    border-radius: 2px;
    background: #0066ff;
  }
}

.etcm__card-actions {
  display: inline-flex;
  align-items: center;
  gap: 10px;
}

.etcm__status-indicator {
  font-size: 12px;
  font-weight: 600;
  color: #9ca3af;
  padding: 3px 10px;
  border-radius: 999px;
  background: #f3f4f6;

  &--active {
    color: #0066ff;
    background: #e8f0fe;
  }
}

.etcm__card-body {
  padding: 14px 16px;
}

.etcm__target-summary {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
  padding: 8px 10px;
  border-radius: 6px;
  background: #f8fafc;
}
.etcm__target-summary-label {
  font-size: 12px;
  font-weight: 700;
  color: #6b7280;
}
.etcm__target-summary-name {
  font-size: 14px;
  color: #1f2937;
}

/* ── 평가자 행 ── */
.etcm__evaluator-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 0;

  & + & {
    border-top: 1px solid #f1f3f5;
  }
}
.etcm__evaluator-row--multi {
  margin-bottom: 8px;
  padding: 6px 0;

  & + & {
    border-top: 1px solid #f1f3f5;
  }

  &:last-child {
    margin-bottom: 0;
  }
}
.etcm__evaluator-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: #e5e7eb;
  color: #6b7280;
  font-size: 11px;
  font-weight: 700;
  flex-shrink: 0;
}
.etcm__evaluator-label {
  font-size: 13px;
  font-weight: 600;
  color: #374151;
  min-width: 32px;
}

/* ── 평가자 사용자 필드 ── */
.etcm__user-field {
  display: flex;
  align-items: center;
  gap: 6px;
  flex: 1;
  min-width: 0;
}
.etcm__evaluator-row--multi .etcm__user-field {
  flex: 1.4;
  padding: 0;
  border: none;
  background: transparent;
  gap: 8px;
}
.etcm__multi-eval-select {
  flex: 1;
  min-width: 0;
}
.etcm__evaluator-row--multi :deep(.etcm__multi-eval-select .ui-multi-select__control) {
  min-height: 36px;
}
.etcm__user-display {
  flex: 1;
  min-height: 32px;
  padding: 6px 10px;
  border: 1px solid #e0e4e8;
  border-radius: 4px;
  background: #fff;
  font-size: 14px;
  color: #464c53;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  &--empty {
    color: #adb5bd;
  }
}
.etcm__chip-list {
  flex: 1;
  min-height: 32px;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
  padding: 4px 8px;
  border: 1px solid #e0e4e8;
  border-radius: 4px;
  background: #fff;
  color: #464c53;

  &--empty {
    color: #adb5bd;
  }
}
.etcm__evaluator-row--multi .etcm__chip-list {
  min-height: 36px;
  border-color: #e5e7eb;
  background: #ffffff;
}
.etcm__user-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  max-width: 160px;
  padding: 3px 10px;
  border: 1px solid #dbeafe;
  border-radius: 999px;
  background: #eff6ff;
  color: #1d4ed8;
  font-size: 12px;
  font-weight: 500;
}
.etcm__user-chip-remove {
  border: 0;
  background: transparent;
  color: #1d4ed8;
  cursor: pointer;
  line-height: 1;
}

/* ── 비율 행 ── */
.etcm__rate-row {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 8px;

  &:last-of-type {
    margin-bottom: 8px;
  }
}
.etcm__ratio-kind {
  width: 44px;
  font-size: 13px;
  font-weight: 600;
  color: #374151;
  flex-shrink: 0;
}
.etcm__rate-input {
  width: 96px !important;
  min-width: 96px;
  max-width: 96px;
  flex: 0 0 96px;
}
.etcm__pct {
  font-weight: 500;
  color: #555;
}

.etcm__guide-message {
  padding: 10px 12px;
  border: 1px solid #fee2e2;
  border-radius: 6px;
  background: #fef2f2;
  color: #b91c1c;
  font-size: 13px;
  font-weight: 600;
}

/* ── 합계 인디케이터 ── */
.etcm__sum-indicator {
  margin-top: 10px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;

  &--ok {
    background: #e6f7ed;
    color: #0a7c42;
  }
  &--warn {
    background: #fef2f2;
    color: #b91c1c;
  }
}

/* ── 역량군 아코디언 ── */
.etcm__capa-accordion {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  margin-bottom: 8px;
  overflow: hidden;

  &:last-of-type {
    margin-bottom: 0;
  }
}
.etcm__capa-accordion-header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  background: #fafbfc;
}
.etcm__capa-accordion-toggle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border: none;
  background: transparent;
  color: #6b7280;
  cursor: pointer;
  border-radius: 4px;

  &:hover {
    background: #eef2ff;
    color: #3b82f6;
  }
}
.etcm__capa-gbn-name {
  font-size: 14px;
  font-weight: 600;
  color: #1f2937;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.etcm__capa-count {
  font-size: 12px;
  color: #6b7280;
  background: #f3f4f6;
  padding: 2px 8px;
  border-radius: 999px;
  flex-shrink: 0;
}
.etcm__capa-rate {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
}

/* ── 역량 항목 칩 ── */
.etcm__capa-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding: 10px 12px 12px;
  background: #ffffff;
  border-top: 1px dashed #e5e7eb;
}
.etcm__capa-chip {
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  border: 1px solid #dbeafe;
  border-radius: 999px;
  background: #eff6ff;
  color: #1d4ed8;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
}
</style>
